<template>
  <div>
    <div class="form-row row">
      <div class="form-column col-xs-12 col-sm-8">
        <label class="label">Flock ID</label>
        <input
          class="input-field"
          :class="{'parsley-error': 'id' in errors}"
          type="text"
          placeholder="Ex: IA1234"
          v-model="flock.id"
          @input="$emit('update', flock)"
        >
        <div
          class="error-text"
          v-if="'id' in errors"
          v-html="errors.id"
        ></div>
      </div>
    </div>
    <div v-if="checkPremise" class="form-row row">
      <div class="form-column col-xs-12 col-sm-8">
        <label class="label">Premise ID</label>
        <input
          class="input-field"
          :class="{'parsley-error': 'premise_id' in errors}"
          type="text"
          pattern="[a-zA-Z0-9]*"
          placeholder="Ex: ABC1234"
          @keydown="alphaNumericOnly"
          v-model="flock.premise_id"
          @input="$emit('update', flock)"
        >
        <div
          class="error-text"
          v-if="'premise_id' in errors"
          v-html="errors.premise_id"
        ></div>
      </div>
    </div>
    <div class="form-row row">
      <div class="form-column col-xs-12 col-sm-8">
        <label class="label">State</label>
        <select
          class="input-field"
          :class="{'parsley-error': 'state' in errors}"
          v-model="flock.address.state"
          @change="$emit('update', flock)">
          <option value="">Select your state</option>
          <option v-for="(state, abbr) in states" :key="`state-${abbr}`" :value="abbr">{{ state }}</option>
        </select>
        <div
          class="error-text"
          v-if="'state' in errors"
          v-html="errors.state"
        ></div>
      </div>
    </div>
    <div class="form-row row">
      <div class="form-column col-xs-12 col-sm-8">
        <label class="label">Zip Code</label>
        <input
          class="input-field"
          :class="{'parsley-error': 'zip' in errors}"
          type="text"
          v-model="flock.address.zip"
          @input="$emit('update', flock)"
        >
        <div
          class="error-text"
          v-if="'zip' in errors"
          v-html="errors.zip"
        ></div>
      </div>
    </div>
    <div
      class="text-tiny text-italic"
      style="font-weight: 400;"
      v-if="!checkPremise"
    >
      Need a Flock ID? Call the USDA at
      <a href="tel:8668732824">(866) 873-2824</a> to confirm.
    </div>
    <div
      v-if="checkPremise"
      class="text-tiny text-italic"
      style="font-weight: 400;"
    >
      Need a Flock ID? Call the USDA at <a href="tel:18668732824">(866) 873-2824</a> to confirm. You can
      <a href="https://www.aphis.usda.gov/animal-disease/traceability/pin" target="_blank">apply for a Premises ID</a>
      through your state’s animal health department.
    </div>
    <div
      class="error-text"
      v-if="'general' in errors"
      v-html="errors.general"
    ></div>
  </div>
</template>

<script>
import { pick } from '../helpers.js';

import { states } from '../data.json';

export default {
  props: {
    checkPremise: {
      default: false,
      type: Boolean,
    },
    flock: {
      type: Object,
      default: () => {
        return {
          id: '',
          premise_id: '',
          address: {
            state: '',
            zip: '',
          },
        };
      },
    },
  },

  data() {
    return {
      errors: {},
      states: {},
    };
  },

  created() {
    this.states = states;
  },

  methods: {
    alphaNumericOnly(e) {
      if(!e.key.match(/^[a-zA-Z0-9]*$/)) {
        e.preventDefault();
        return false;
      }
    },

    async validate() {
      let address, sequences;

      this.errors = {};

      if(this.flock.id.trim() === '') {
        this.$set(this.errors, 'id', 'Flock ID is required');

        return false;
      }

      if(this.checkPremise && this.flock.premise_id.trim() === '') {
        this.$set(this.errors, 'premise_id', 'Premise ID is required');

        return false;
      }

      if(this.flock.address.state.trim() === '') {
        this.$set(this.errors, 'state', 'You must select a state');

        return false;
      }

      if(this.flock.address.zip.trim() === '') {
        this.$set(this.errors, 'zip', 'You must enter a valid zip code');

        return false;
      }

      // run API lookup
      await this.$http.get('/flock/lookup', {
        params: {
          id: this.flock.id,
          premise_id: this.flock.premise_id,
          state: this.flock.address.state,
          zip: this.flock.address.zip,
        },
      })
      .then(response => response.data)
      .then(data => {
        sequences = data.order_summary || [];

        address = Object.assign(
          this.flock.address,
          pick(data.address, ['first_name', 'last_name', 'street', 'city', 'state', 'zip'])
        );
      })
      .catch(error => {
        if((error.response || {}).status === 404) {
          this.$set(this.errors, 'general', '<strong>Flock identification could not be found.</strong> Please check that the Flock ID you entered is correct and you’ve entered the correct State and Zip Code that your flock was registered in. Flock IDs often include the State abbreviation as the prefix <em>(e.g. TX1234 for Texas.)</em> Please call <a style="white-space: nowrap;" class="text-bold" href="tel:8668732824">(866) 873-2824</a> to confirm your Flock ID.');
        } else {
          this.$set(this.errors, 'general', 'There was a error retrieving your information from the USDA. Please try again later. If this condition persists, please call <a class="text-bold text-red" href="tel:8002826631">(800) 282-6631</a> to complete your order.');

          if((error.response || {}).status === 422) {
            const errors = error.response.data.errors;

            for(const field in errors) {
              this.$set(this.errors, field, errors[field][0]);
            }
          }
        }
      });

      if(Object.keys(this.errors).length > 0) {
        return false;
      }

      this.$emit('update', Object.assign(this.flock, {address: address}));

      this.$emit('store-sequences', sequences);

      return true;
    },
  },
}
</script>
